// src/pages/BrandSettings.jsx — Modern Executive Brand Management with Live Preview & Sticky Bar
import React, { useEffect, useState, useMemo, useRef } from "react";
import { Plus, Edit2, Upload, X, Globe } from "lucide-react";
import api from "../services/api";
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";
import "./BrandSettings.css";

const PRESET_COLORS = [
  "#0A84FF", // Electric Blue
  "#30D158", // Emerald Green
  "#BF5AF2", // Royal Purple
  "#FF9F0A", // Amber Gold
  "#FF453A", // Crimson Red
  "#64748B"  // Slate Gray
];

const DEFAULT_BRAND_FORM = {
  name: "",
  color: "#0A84FF",
  logo_url: "",
  favicon_url: "",
  tagline: "",
  website: "",
  isActive: true
};

export default function BrandSettings() {
  const { user } = useAuth();
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(DEFAULT_BRAND_FORM);
  const [initialForm, setInitialForm] = useState(DEFAULT_BRAND_FORM);
  const [saving, setSaving] = useState(false);

  const logoInputRef = useRef(null);
  const faviconInputRef = useRef(null);

  const isAdmin = user?.role === "Admin";

  const loadBrands = async () => {
    setLoading(true);
    try {
      const res = await api.get("/brands?all=true");
      setBrands(Array.isArray(res.data) ? res.data : []);
    } catch {
      toast.error("Failed to load brands");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBrands();
  }, []);

  const openAdd = () => {
    const fresh = { ...DEFAULT_BRAND_FORM };
    setForm(fresh);
    setInitialForm(fresh);
    setEditId(null);
    setShowEditor(true);
  };

  const openEdit = (b) => {
    const editData = {
      name: b.name || "",
      color: b.color || "#0A84FF",
      logo_url: b.logo_url || "",
      favicon_url: b.favicon_url || "",
      tagline: b.tagline || "",
      website: b.website || "",
      isActive: Boolean(b.isActive)
    };
    setForm(editData);
    setInitialForm(editData);
    setEditId(b.id);
    setShowEditor(true);
  };

  const isDirty = useMemo(() => {
    return JSON.stringify(form) !== JSON.stringify(initialForm);
  }, [form, initialForm]);

  const handleResetForm = () => {
    setForm({ ...initialForm });
    toast.success("Changes reset to original");
  };

  const handleFileUpload = (field) => (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error("File size must be under 2MB");
      return;
    }

    const reader = new FileReader();
    reader.onload = (ev) => {
      setForm((p) => ({ ...p, [field]: ev.target.result }));
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async (e) => {
    if (e) e.preventDefault();
    if (!form.name?.trim()) {
      toast.error("Brand name is required");
      return;
    }

    setSaving(true);
    try {
      if (editId) {
        await api.put(`/brands/${editId}`, form);
        toast.success("Brand updated successfully");
      } else {
        await api.post("/brands", form);
        toast.success("Brand created successfully");
      }
      setShowEditor(false);
      loadBrands();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Error saving brand");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (b) => {
    try {
      await api.put(`/brands/${b.id}`, { isActive: !b.isActive });
      setBrands((prev) =>
        prev.map((x) => (x.id === b.id ? { ...x, isActive: !x.isActive ? 1 : 0 } : x))
      );
      toast.success(b.isActive ? "Brand deactivated" : "Brand activated");
    } catch {
      toast.error("Error updating brand status");
    }
  };

  if (!isAdmin) {
    return (
      <div className="crm-brand-page text-center py-5">
        <p className="text-muted">Only Admins can manage brands and business units.</p>
      </div>
    );
  }

  return (
    <div className="crm-brand-page">
      {/* Top Toolbar */}
      <div className="crm-brand-toolbar">
        <div>
          <p className="crm-brand-desc">
            Manage brands, logos, color codes, and business units across all CRM leads.
          </p>
        </div>
        <button type="button" className="crm-brand-add-btn" onClick={openAdd}>
          <Plus size={15} /> Add Brand
        </button>
      </div>

      {/* Brands Table */}
      <div className="crm-brand-card">
        <table className="crm-brand-table">
          <thead>
            <tr>
              <th className="crm-brand-th-color">Color</th>
              <th className="crm-brand-th-name">Brand Name & Identity</th>
              <th className="crm-brand-th-status">Status</th>
              <th className="crm-brand-th-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              [1, 2, 3, 4].map((n) => (
                <tr key={n}>
                  <td><div className="crm-skeleton-pulse" style={{ width: 28, height: 28, borderRadius: 8 }} /></td>
                  <td><div className="crm-skeleton-pulse" style={{ width: 180, height: 16 }} /></td>
                  <td><div className="crm-skeleton-pulse" style={{ width: 70, height: 22, borderRadius: 12 }} /></td>
                  <td><div className="crm-skeleton-pulse" style={{ width: 100, height: 28, borderRadius: 6, marginLeft: "auto" }} /></td>
                </tr>
              ))
            ) : brands.length === 0 ? (
              <tr>
                <td colSpan={4} style={{ textAlign: "center", color: "var(--muted)", padding: "32px" }}>
                  No brands configured yet. Click "+ Add Brand" to create one.
                </td>
              </tr>
            ) : (
              brands.map((b) => (
                <tr key={b.id}>
                  {/* Color Swatch */}
                  <td>
                    <div className="crm-brand-color-cell">
                      <span
                        className="crm-brand-swatch"
                        style={{ backgroundColor: b.color || "#0A84FF" }}
                        title={b.color || "#0A84FF"}
                      />
                    </div>
                  </td>

                  {/* Brand Identity & Logo Preview */}
                  <td>
                    <div className="crm-brand-identity-cell">
                      {b.logo_url ? (
                        <img src={b.logo_url} alt={b.name} className="crm-brand-logo-thumb" />
                      ) : (
                        <div
                          className="crm-brand-logo-fallback"
                          style={{ backgroundColor: b.color || "#0A84FF" }}
                        >
                          {b.name?.[0]?.toUpperCase() || "B"}
                        </div>
                      )}
                      <div>
                        <div className="crm-brand-name-title">{b.name}</div>
                        {b.tagline && (
                          <div className="crm-brand-name-sub">{b.tagline}</div>
                        )}
                      </div>
                    </div>
                  </td>

                  {/* Status Badge */}
                  <td>
                    <span className={`crm-brand-badge ${b.isActive ? "active" : "inactive"}`}>
                      <span className={`crm-brand-dot ${b.isActive ? "green" : "gray"}`} />
                      {b.isActive ? "Active" : "Inactive"}
                    </span>
                  </td>

                  {/* Actions (Guaranteed No Wrap) */}
                  <td>
                    <div className="crm-brand-actions">
                      <button
                        type="button"
                        className="crm-brand-btn-edit"
                        onClick={() => openEdit(b)}
                        title="Edit brand settings"
                      >
                        <Edit2 size={13} />
                      </button>
                      <button
                        type="button"
                        className={`crm-brand-btn-toggle ${b.isActive ? "deactivate" : "activate"}`}
                        onClick={() => toggleActive(b)}
                      >
                        {b.isActive ? "Deactivate" : "Activate"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ── Brand Editor Modal ── */}
      {showEditor && (
        <div className="crm-brand-modal-overlay" onClick={() => setShowEditor(false)}>
          <div className="crm-brand-modal" onClick={(e) => e.stopPropagation()}>
            <div className="crm-brand-modal-header">
              <h3>{editId ? `Edit Brand: ${initialForm.name}` : "Create New Brand"}</h3>
              <button
                type="button"
                className="crm-brand-modal-close"
                onClick={() => setShowEditor(false)}
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSave}>
              <div className="crm-brand-modal-body">
                {/* 1. Brand Assets (Logo & Favicon) */}
                <div className="crm-brand-section">
                  <div className="crm-brand-section-title">1. BRAND ASSETS & PREVIEWS</div>
                  <div className="crm-brand-upload-grid">
                    {/* Logo Dropzone */}
                    <div
                      className="crm-brand-dropzone"
                      onClick={() => logoInputRef.current?.click()}
                    >
                      {form.logo_url ? (
                        <>
                          <img src={form.logo_url} alt="Logo Preview" className="crm-brand-preview-img" />
                          <button
                            type="button"
                            className="crm-brand-upload-remove-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              setForm((p) => ({ ...p, logo_url: "" }));
                            }}
                          >
                            Remove Logo
                          </button>
                        </>
                      ) : (
                        <>
                          <Upload size={20} className="text-muted" style={{ marginBottom: 6 }} />
                          <span className="crm-brand-upload-text"><strong>Upload Brand Logo</strong> (PNG / SVG)</span>
                        </>
                      )}
                      <input
                        type="file"
                        ref={logoInputRef}
                        accept="image/*"
                        style={{ display: "none" }}
                        onChange={handleFileUpload("logo_url")}
                      />
                    </div>

                    {/* Favicon Dropzone */}
                    <div
                      className="crm-brand-dropzone"
                      onClick={() => faviconInputRef.current?.click()}
                    >
                      {form.favicon_url ? (
                        <>
                          <img src={form.favicon_url} alt="Favicon Preview" className="crm-brand-preview-favicon" />
                          <button
                            type="button"
                            className="crm-brand-upload-remove-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              setForm((p) => ({ ...p, favicon_url: "" }));
                            }}
                          >
                            Remove Favicon
                          </button>
                        </>
                      ) : (
                        <>
                          <Globe size={20} className="text-muted" style={{ marginBottom: 6 }} />
                          <span className="crm-brand-upload-text"><strong>Upload Favicon</strong> (32x32 / ICO)</span>
                        </>
                      )}
                      <input
                        type="file"
                        ref={faviconInputRef}
                        accept="image/*"
                        style={{ display: "none" }}
                        onChange={handleFileUpload("favicon_url")}
                      />
                    </div>
                  </div>
                </div>

                {/* 2. Brand Identity */}
                <div className="crm-brand-section">
                  <div className="crm-brand-section-title">2. BRAND IDENTITY & DETAILS</div>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>
                      Brand Name *
                    </label>
                    <input
                      type="text"
                      className="crm-brand-input"
                      placeholder="e.g. Platinum Auto Japan"
                      value={form.name}
                      onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                      required
                      autoFocus
                    />
                  </div>

                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>
                      Tagline / Slogan
                    </label>
                    <input
                      type="text"
                      className="crm-brand-input"
                      placeholder="e.g. Exporting Quality Vehicles Worldwide"
                      value={form.tagline}
                      onChange={(e) => setForm((p) => ({ ...p, tagline: e.target.value }))}
                    />
                  </div>

                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>
                      Website URL
                    </label>
                    <input
                      type="text"
                      className="crm-brand-input"
                      placeholder="e.g. https://platinumautojapan.com"
                      value={form.website}
                      onChange={(e) => setForm((p) => ({ ...p, website: e.target.value }))}
                    />
                  </div>
                </div>

                {/* 3. Theme Color & Status */}
                <div className="crm-brand-section">
                  <div className="crm-brand-section-title">3. THEME COLOR & STATUS</div>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 6 }}>
                      Brand Accent Color
                    </label>
                    <div className="crm-brand-color-picker-row">
                      <input
                        type="color"
                        value={form.color || "#0A84FF"}
                        onChange={(e) => setForm((p) => ({ ...p, color: e.target.value }))}
                        className="crm-brand-native-color"
                      />
                      <input
                        type="text"
                        value={form.color || "#0A84FF"}
                        onChange={(e) => setForm((p) => ({ ...p, color: e.target.value }))}
                        className="crm-brand-hex-input"
                        placeholder="#0A84FF"
                      />
                      <div className="crm-brand-preset-swatches">
                        {PRESET_COLORS.map((clr) => (
                          <div
                            key={clr}
                            className={`crm-brand-preset-chip ${form.color?.toLowerCase() === clr.toLowerCase() ? "selected" : ""}`}
                            style={{ backgroundColor: clr }}
                            onClick={() => setForm((p) => ({ ...p, color: clr }))}
                            title={clr}
                          />
                        ))}
                      </div>
                    </div>
                  </div>

                  <div style={{ marginTop: 10 }}>
                    <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
                      <input
                        type="checkbox"
                        checked={form.isActive}
                        onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.checked }))}
                        style={{ width: 16, height: 16, accentColor: "var(--primary)" }}
                      />
                      <span style={{ fontSize: 13, fontWeight: 500 }}>Active (Available for lead tags & assignment)</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* ── Sticky Save / Reset Bar ── */}
              <div className="crm-brand-sticky-bar">
                <div className="crm-brand-dirty-notice">
                  {isDirty ? (
                    <>
                      <span className="crm-brand-dirty-dot" />
                      <span>Unsaved changes detected</span>
                    </>
                  ) : (
                    <span>All changes saved</span>
                  )}
                </div>

                <div className="crm-brand-bar-actions">
                  <button
                    type="button"
                    className="crm-brand-btn-reset"
                    onClick={handleResetForm}
                    disabled={!isDirty || saving}
                  >
                    Reset
                  </button>
                  <button
                    type="submit"
                    className="crm-brand-btn-save"
                    disabled={!form.name?.trim() || saving}
                  >
                    {saving ? "Saving..." : editId ? "Save Changes" : "Create Brand"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
